/** /translation_glossary/ — per-tenant translation glossary (metaplan A6, AIE §8 round 4 Q2).
 *
 * Every machine translation of a tenant applies its glossary: a `keep` term is kept verbatim
 * in every language, a `translate` term is replaced by the tenant's preferred translation for
 * the target language (`translations: {iso: text}`). The tenant's own name is always kept
 * verbatim (not stored). Ops Admin only by default (`console_translation_glossary_view` /
 * `_manage`).
 *
 * Hand-authored wrapper (console convention). Writes are last-writer-wins.
 */
import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { TenantRef } from './tours';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

export type GlossaryKind = 'keep' | 'translate';

export interface GlossaryTerm {
  id: number;
  tenant: TenantRef;
  /** The term as it appears in source text. */
  term: string;
  /** The language the term is written in; '' = any source language. */
  source_lang: string;
  kind: GlossaryKind;
  /** Preferred translation per target language (kind `translate`). */
  translations: Record<string, string>;
  case_sensitive: boolean;
  enabled: boolean;
  notes: string;
  created_by: { id: number; email: string | null } | null;
  created_ts: string;
  updated_ts: string;
}

export interface GlossaryTermWrite {
  tour_operator?: number;
  term?: string;
  source_lang?: string;
  kind?: GlossaryKind;
  translations?: Record<string, string>;
  case_sensitive?: boolean;
  enabled?: boolean;
  notes?: string;
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  /** term + notes (icontains). */
  search?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  kind?: GlossaryKind;
  enabled?: boolean;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) usp.set('tour_operator_id', String(params.tour_operator_id));
  if (params.kind) usp.set('kind', params.kind);
  if (params.enabled !== undefined) usp.set('enabled', String(params.enabled));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<GlossaryTerm>> {
  return apiGet<PaginatedResponse<GlossaryTerm>>(`/translation_glossary/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<GlossaryTerm> {
  return apiGet<GlossaryTerm>(`/translation_glossary/${id}/`);
}

export function create(body: GlossaryTermWrite): Promise<GlossaryTerm> {
  return apiPost<GlossaryTerm>('/translation_glossary/', body);
}

export function update(id: number, body: GlossaryTermWrite): Promise<GlossaryTerm> {
  return apiPatch<GlossaryTerm>(`/translation_glossary/${id}/`, body);
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/translation_glossary/${id}/`);
}
